// Util bersama untuk import dokumen legacy (PRN) di halaman Purchase:
// dipakai ImportLegacyDO (DO lama) dan ImportFactorySuratJalan (SJ pabrik).

export const apiBase = () => (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")

export const stripControlChars = (value) =>
  String(value || "")
    .split("")
    .map((char) => {
      const code = char.charCodeAt(0)
      return (code < 32 || (code >= 127 && code <= 159)) ? " " : char
    })
    .join("")

export const normalizeText = (value) =>
  stripControlChars(value)
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase()

export const parseLegacyDate = (value) => {
  const match = String(value || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/)
  if (!match) return ""

  const day = match[1].padStart(2, "0")
  const month = match[2].padStart(2, "0")
  const rawYear = match[3]
  const year = rawYear.length === 2 ? `20${rawYear}` : rawYear

  return `${year}-${month}-${day}`
}

// Tanggal bisnis pakai zona WIB (Asia/Jakarta, UTC+7). toISOString() memakai UTC
// sehingga jam 00:00-06:59 WIB masih terhitung tanggal kemarin — salah untuk PO/cost.
export const todayIso = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date())

export const pickProductId = (row) => row?.product_unit_id ?? row?.id ?? ""

export const pickCostPrice = (row) => {
  const candidates = [
    row?.price,
    row?.pcs_price,
    row?.maximum_retail_price,
    row?.purchase_price,
    row?.buy_price,
    row?.cost_price,
    row?.hpp,
  ]

  for (const candidate of candidates) {
    const value = Number(candidate)
    if (Number.isFinite(value) && value > 0) return value
  }

  return 0
}

export const extractRows = (json) => {
  if (Array.isArray(json?.data)) return json.data
  if (Array.isArray(json?.data?.data)) return json.data.data
  if (Array.isArray(json?.data?.data?.data)) return json.data.data.data
  return []
}

export const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result || "")
    reader.onerror = () => reject(reader.error || new Error("Gagal membaca file"))
    reader.readAsText(file)
  })

// Map nama satuan (normalized) -> qty_per_unit dari master Satuan.
export const fetchSatuanMap = async () => {
  const base = apiBase()
  if (!base) return new Map()

  const response = await fetch(`${base}/satuan?paginate=false`)
  const json = await response.json().catch(() => ({}))
  if (!response.ok) return new Map()

  const rows = Array.isArray(json?.data) ? json.data : (Array.isArray(json?.data?.data) ? json.data.data : [])

  const map = new Map()
  for (const row of rows) {
    const key = normalizeText(row?.name)
    const qty = Number(row?.qty_per_unit)
    if (key && Number.isFinite(qty) && qty > 0) {
      map.set(key, qty)
    }
  }
  return map
}

// Resolve nama produk legacy -> product_unit via legacy_product_unit_mappings.
// Return Map keyed normalized legacy name, hanya berisi baris yang mapped.
export const resolveLegacyProductMappings = async (productNames, outletId) => {
  const base = apiBase()
  const names = Array.from(
    (productNames || [])
      .reduce((map, name) => {
        const key = normalizeText(name)
        if (key && !map.has(key)) map.set(key, name)
        return map
      }, new Map())
      .values()
  )

  if (!base || !names.length) return new Map()

  const result = new Map()

  // Endpoint membatasi 500 nama per request.
  for (let start = 0; start < names.length; start += 400) {
    const response = await fetch(`${base}/legacy-product-unit-mappings/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        names: names.slice(start, start + 400),
        outlet_id: outletId || "",
        // Harga cost selalu pakai harga paling fresh per hari ini (bukan tanggal DO),
        // konsisten dengan aturan "freshest active_date <= today".
        active_date: todayIso(),
      }),
    })

    const json = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(json?.message || "Gagal membaca mapping produk lama")

    const rows = Array.isArray(json?.data) ? json.data : []

    for (const row of rows) {
      const key = normalizeText(row?.legacy_name || row?.normalized_legacy_product_name)
      if (key && row?.mapped && pickProductId(row)) result.set(key, row)
    }
  }

  return result
}

// Cek apakah No.Bukti legacy sudah pernah diimport jadi PO.
// Return { exists, po } dari backend.
export const checkLegacyBukti = async (noBukti) => {
  const base = apiBase()
  const params = new URLSearchParams({ no_bukti: noBukti })
  const response = await fetch(`${base}/purchase-orders/check-legacy-bukti?${params.toString()}`)
  const json = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(json?.message || "Gagal cek duplikat No.Bukti")
  return json
}

export const fetchGoodsReceiptForPo = async (poId) => {
  const base = apiBase()
  const params = new URLSearchParams({
    po_id: poId,
    status: "DRAFT",
    paginate: "false",
    per_page: "1",
  })

  const response = await fetch(`${base}/goods-receipts?${params.toString()}`)
  const json = await response.json().catch(() => ({}))

  if (!response.ok) throw new Error(json?.message || "Gagal mengambil Goods Receipt")

  return extractRows(json)[0] || null
}

export const updateGoodsReceiptHeader = async (receiptId, payload) => {
  const base = apiBase()
  const response = await fetch(`${base}/goods-receipts/${receiptId}/header`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })

  const json = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(json?.message || "Gagal update header Goods Receipt")
  return json
}
